import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Layout, Database, Shield, Zap, Globe, Cpu, ArrowRight } from 'lucide-react';
import './ServicesPage.css';

const ServicesPage = ({ onOpenForm }) => {
  const mainServices = [
    {
      title: "Web Development",
      description: "We build scalable, high-performance web applications using modern frameworks. Our focus is on speed, security, and exceptional user experience.",
      icon: <Globe className="service-icon" />,
      features: ["Custom SaaS Platforms", "E-commerce Solutions", "Progressive Web Apps", "Enterprise Systems"],
      color: "59, 130, 246"
    },
    {
      title: "AI Solutions",
      description: "Integrating intelligent automation and LLMs into your business workflow. We help you leverage data-driven insights and AI-powered tools.",
      icon: <Brain className="service-icon" />,
      features: ["LLM Integration", "Predictive Analytics", "AI Chatbots", "Process Automation"],
      color: "139, 92, 246"
    },
    {
      title: "UI/UX Design",
      description: "Clean, conversion-focused interfaces that wow your users. We combine aesthetics with usability to create memorable digital journeys.",
      icon: <Layout className="service-icon" />,
      features: ["User Research", "Wireframing", "Prototyping", "Visual Identity"],
      color: "236, 72, 153"
    },
    {
      title: "Dashboard Systems",
      description: "Powerful admin panels and business management tools. Visualize your data and manage your operations with precision.",
      icon: <Cpu className="service-icon" />,
      features: ["Real-time Analytics", "Custom Dashboards", "User Management", "Reporting Tools"],
      color: "16, 185, 129"
    },
    {
      title: "API & Backend",
      description: "Secure, robust backend architecture that powers your application. We design APIs that are scalable and easy to integrate.",
      icon: <Database className="service-icon" />,
      features: ["RESTful & GraphQL", "Microservices", "Cloud Infrastructure", "Database Design"],
      color: "245, 158, 11"
    }
  ];

  return (
    <motion.div 
      className="services-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <section className="services-hero">
        <div className="container">
          <motion.div
            className="services-hero-eyebrow"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            What We Build
          </motion.div>
          <motion.h1 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Solutions Engineered for <span>Performance</span>
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            We combine technical expertise with business logic to deliver high-impact digital solutions.
          </motion.p>
        </div>
      </section>

      <section className="services-grid-section">
        <div className="container">
          <div className="services-detailed-grid">
            {mainServices.map((service, index) => (
              <motion.div 
                key={index}
                className="service-detail-card"
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="sd-icon-wrapper" style={{ '--accent': service.color }}>
                  {service.icon}
                </div>
                <div className="sd-content">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                </div>
                <ul className="sd-features">
                  {service.features.map((f, i) => (
                    <li key={i}><Shield size={14} /> {f}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="counseling-cta">
        <div className="container">
          <div className="counseling-box">
            <div className="cb-content">
              <h2>Schedule a <span>Technical Counseling</span></h2>
              <p>Not sure where to start? Talk to our lead engineers about your project requirements and technical roadmap.</p>
              <div className="cb-perks">
                <div className="cb-perk"><Zap size={16} /> 30-min Strategy Call</div>
                <div className="cb-perk"><Shield size={16} /> Technical Audit</div>
                <div className="cb-perk"><Cpu size={16} /> Cost Estimation</div>
              </div>
            </div>
            <div className="cb-action">
              <div className="start-project-card" onClick={onOpenForm}>
                <h3>Start Your Project</h3>
                <p>Launch your inquiry and get a response within 24 hours.</p>
                <button className="btn btn-primary w-full">Begin Inquiry <ArrowRight size={18} /></button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default ServicesPage;
